import i18n from "@/i18n";

/**
 * Plain `fetch`, not the generated client: these are excluded from the OpenAPI document, because
 * a sign-in flow is not an API anyone integrates against.
 */
export type LocalSignInStatus =
  | "code"
  | "aangemeld"
  | "verkeerde-code"
  | "geblokkeerd"
  | "tweede-factor-geblokkeerd"
  | "mislukt";

export interface LocalSignInResponse {
  status: LocalSignInStatus;
  recoveryCodes?: string[];
}

/** The account page's one transaction, as the API answers it. */
export type SetPasswordStatus =
  | "gezet"
  | "verkeerde-code"
  | "geblokkeerd"
  | "datalek"
  | "al-gezet"
  | "geweigerd"
  | "verwijderd";

/** Identical for a known address, an unknown one and a shared one alike. */
export type InloglinkRequestStatus =
  | "verstuurd"
  | "te-vaak"
  | "niet-beschikbaar";

export interface InloglinkRequestResponse {
  status: InloglinkRequestStatus;
}

/** <c>ongeldig</c> covers expired, spent, unknown and blocked - a caller may not learn which. */
export type UseInloglinkStatus = "aangemeld" | "geen-persoon" | "ongeldig";

export interface UseInloglinkResponse {
  status: UseInloglinkStatus;
  returnUrl?: string | null;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(
      problem?.error ??
        i18n.t("common:errors.unexpected", { status: response.status }),
    );
  }

  return (await response.json()) as T;
}

export const signInWithPassword = (email: string, password: string) =>
  post<LocalSignInResponse>("/api/auth/local/sign-in", { email, password });

export const submitCode = (code: string) =>
  post<LocalSignInResponse>("/api/auth/local/two-factor", { code });

export const requestInloglink = (email: string, returnUrl?: string) =>
  post<InloglinkRequestResponse>("/api/auth/inloglink/aanvragen", {
    email,
    returnUrl,
  });

export const consumeInloglink = (nonce: string, token: string) =>
  post<UseInloglinkResponse>("/api/auth/inloglink/gebruik", { nonce, token });

/**
 * Failures that could reveal whether an address holds an account share one wording, as the API
 * does. A function, not a table: a table is built before i18next initialises and holds the keys.
 */
export function localSignInMessage(status: LocalSignInStatus) {
  switch (status) {
    case "verkeerde-code":
      return i18n.t("session:local.errors.wrongCode");
    case "geblokkeerd":
      return i18n.t("session:local.errors.blocked");
    case "tweede-factor-geblokkeerd":
      return i18n.t("session:local.errors.twoFactorBlocked", {
        mailbox: i18n.t("common:mailbox"),
      });
    case "mislukt":
      return i18n.t("session:local.errors.failed");
    // The two that are not failures: the flow moves on and nothing is said.
    case "code":
    case "aangemeld":
      return "";
  }
}
